import { motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'

import { GameBackdrop } from '@/features/stadium/GameBackdrop'
import { SparkBurst } from '@/features/stadium/SparkBurst'
import type { Bracket, BracketMatch, RGNEvent } from '@/lib/api/schemas'

/**
 * COMPLETE phase — three-podium reveal with spark-burst on 1st arrival.
 *
 * Visual layout (centred at the bottom, ascending podiums):
 *
 *   ┌──────────┐
 *   │ AVATAR   │
 *   │ Champion │
 *   │ £X prize │     ┌──────────┐
 *   │          │     │ AVATAR   │
 *   │          │     │ Runner   │     ┌──────────┐
 *   │  ▰▰▰▰▰▰  │     │ £X prize │     │ AVATAR   │
 *   │  ▰▰▰▰▰▰  │     │  ▰▰▰▰▰▰  │     │ 3rd      │
 *   │  ▰▰▰▰▰▰  │     │  ▰▰▰▰▰▰  │     │  ▰▰▰▰▰▰  │
 *   └──────────┘     └──────────┘     └──────────┘
 *      1st              2nd              3rd
 *      gold             silver           bronze
 *
 * Reveal order (per Emil framework — broadcast pacing):
 *   t=0     : 3rd rises from below (700ms spring, bounce 0.22)
 *   t=900   : 2nd rises
 *   t=1800  : 1st rises  →  spark-burst fires on 1st settle
 *
 * Each podium is `translateY(80%) + opacity 0 → translateY(0) + opacity 1`
 * with a subtle spring bounce. Per-podium duration is 700ms but the
 * spring rules the perceived feel — bounce: 0.22 is the high end of
 * Emil's "subtle" range, justified by this being the peak moment.
 */
export function CompletePhase({
  event,
  bracket,
}: {
  event: RGNEvent
  /** Fallback source when `event.finalStandings` hasn't been
   *  snapshotted yet (race between the last winner-report and the
   *  cron that writes finalStandings). The bracket has enough to
   *  derive a top-3, so we never strand on "STANDINGS PENDING". */
  bracket?: Bracket | null
}) {
  const podiums = useMemo(() => buildPodiums(event, bracket ?? null), [event, bracket])
  const [sparkActive, setSparkActive] = useState(false)
  // One-shot guard. `podiums` gets a new array reference on every 5s
  // poll cycle (useMemo dep on event/bracket), which would otherwise
  // re-fire the burst on every poll. The ref persists across renders
  // without triggering them.
  const sparkFiredRef = useRef(false)

  // Fire the spark-burst as the 1st-place podium settles. Stagger
  // index for 1st is 2 (0=3rd, 1=2nd, 2=1st) × 900ms + the spring
  // settle (~600ms) = ~2.4s after mount.
  useEffect(() => {
    if (sparkFiredRef.current) return
    if (podiums.length === 0) return
    const goldRank = podiums.find((p) => p.rank === 1)
    if (!goldRank) return
    sparkFiredRef.current = true
    const goldOrderIndex = podiums.indexOf(goldRank)
    const fireAtMs = goldOrderIndex * 900 + 500 // launch slightly into the spring settle
    const t = setTimeout(() => setSparkActive(true), fireAtMs)
    return () => clearTimeout(t)
  }, [podiums])

  return (
    <div className="relative flex h-full flex-col px-[clamp(16px,2vw,40px)]">
      <GameBackdrop event={event} />

      {/* Header + podium content sit on z-10 so the spark-burst (z-0,
          absolute within this root) radiates from BEHIND them — rays
          frame the champion instead of covering them. */}
      <header className="relative z-10 flex flex-col items-center pt-[clamp(20px,4vh,60px)]">
        <span
          className="font-display font-extrabold uppercase"
          style={{
            color: 'var(--tv-gold)',
            letterSpacing: '0.35em',
            fontSize: 'clamp(0.9rem, 1.15vw, 1.2rem)',
          }}
        >
          Tournament Complete
        </span>
        <h2
          className="font-display mt-3 text-center font-black leading-tight"
          style={{
            fontSize: 'clamp(2.2rem, 4.5vw, 4.5rem)',
            maxWidth: '24ch',
          }}
        >
          {event.title || 'Tournament'}
        </h2>
      </header>

      {/* Podium row, centred + bottom-aligned. Visual order
          left-to-right: 2nd, 1st, 3rd. Stagger order temporal:
          3rd reveals first, 2nd, then 1st. z-10 lifts above the
          spark-burst canvas. */}
      <div className="relative z-10 flex flex-1 items-end justify-center gap-[clamp(20px,2vw,48px)] pb-[clamp(40px,6vh,80px)]">
        {podiums.length === 0 ? (
          <EmptyState />
        ) : (
          podiums.map((podium, i) => (
            <PodiumColumn key={podium.rank} podium={podium} delay={i * 0.9} />
          ))
        )}
      </div>

      {sparkActive && (
        <SparkBurst
          active
          onDone={() => {
            // Clear active so the canvas unmounts after the animation.
            // sparkFiredRef stays true so re-renders from poll ticks
            // don't re-arm the timer — it's a true one-shot.
            setSparkActive(false)
          }}
        />
      )}
    </div>
  )
}

interface PodiumEntry {
  rank: number
  heightClass: string
  accent: string
  glowColor: string
  label: string
  teamName: string
  avatars: { userId: string; url: string | null; initials: string }[]
}

function buildPodiums(
  event: RGNEvent,
  bracket: Bracket | null,
): PodiumEntry[] {
  const standings =
    event.finalStandings && event.finalStandings.length > 0
      ? event.finalStandings
      : deriveStandingsFromBracket(bracket)
  // Reveal order: 3rd → 2nd → 1st. Filter to ranks 1-3, sort by
  // ascending rank-from-top (so 3 fires first, 1 fires last).
  const top = standings
    .filter((s) => s.rank >= 1 && s.rank <= 3)
    .sort((a, b) => b.rank - a.rank) // 3rd first in array → first to render
  return top.map((s) => {
    const meta = PODIUM_META[s.rank]
    return {
      rank: s.rank,
      heightClass: meta.heightClass,
      accent: meta.accent,
      glowColor: meta.glow,
      label: s.placementLabel ?? meta.label,
      teamName: s.teamLabel?.trim() || s.members[0]?.displayName || 'Team',
      avatars: s.members.slice(0, 4).map((m) => ({
        userId: m.userId,
        url: m.avatarUrl ?? null,
        initials: (m.displayName ?? '?').slice(0, 2).toUpperCase(),
      })),
    }
  })
}

/**
 * Fallback podium derivation — pulls the top 3 from the bracket data
 * when `event.finalStandings` hasn't been snapshotted yet (the
 * backend writes it lazily on the cron after the last match clears).
 *
 * Strategy for a single-elimination bracket:
 *   - 1st: winner of the highest-round match
 *   - 2nd: loser of the highest-round match
 *   - 3rd: loser(s) of the second-highest-round matches; if there's
 *          a tagged Bronze Match, use its winner instead
 *
 * Returns the shape `event.finalStandings` would carry, so the
 * caller's mapping works without changes.
 */
type DerivedStanding = {
  rank: number
  teamId: string
  teamLabel?: string | null
  placementLabel?: string | null
  members: { userId: string; displayName?: string | null; avatarUrl?: string | null }[]
}

function deriveStandingsFromBracket(bracket: Bracket | null): DerivedStanding[] {
  if (!bracket?.matches) return []
  const winners = bracket.matches.filter((m) => m.kind === 'W' || m.kind === 'GF')
  if (winners.length === 0) return []
  const maxRound = winners.reduce(
    (max, m) => (typeof m.round === 'number' && m.round > max ? m.round : max),
    -Infinity,
  )
  const finalMatch =
    winners.find((m) => m.kind === 'GF' && isComplete(m)) ??
    winners.find((m) => m.round === maxRound && isComplete(m))
  if (!finalMatch) return []

  const standings: DerivedStanding[] = []
  const winningTeam = matchWinner(finalMatch)
  const losingTeam = matchLoser(finalMatch)
  if (winningTeam) standings.push(toStanding(winningTeam, 1))
  if (losingTeam) standings.push(toStanding(losingTeam, 2))

  // 3rd place — prefer an explicit bronze match if present, otherwise
  // share rank 3 across the semifinal losers (mirrors the backend's
  // `thirdPlacePlayoff: false` semantic).
  const bronze = bracket.matches.find((m) => m.kind === 'BR' && isComplete(m))
  if (bronze) {
    const bronzeWinner = matchWinner(bronze)
    if (bronzeWinner) standings.push(toStanding(bronzeWinner, 3))
  } else {
    const semiLosers = winners
      .filter((m) => m.round === maxRound - 1 && isComplete(m))
      .map(matchLoser)
      .filter((t): t is NonNullable<typeof t> => !!t)
    for (const t of semiLosers) standings.push(toStanding(t, 3))
  }
  return standings
}

function isComplete(m: BracketMatch): boolean {
  const s = (m.status ?? '').toUpperCase()
  return s === 'COMPLETE' || s === 'COMPLETED' || s === 'OVERRIDDEN'
}

function matchWinner(m: BracketMatch) {
  const wid = m.winnerTeamId
  if (!wid) return null
  if (m.participantA?.teamId === wid) return m.participantA
  if (m.participantB?.teamId === wid) return m.participantB
  return null
}

function matchLoser(m: BracketMatch) {
  const wid = m.winnerTeamId
  if (!wid) return null
  if (m.participantA?.teamId === wid) return m.participantB ?? null
  if (m.participantB?.teamId === wid) return m.participantA ?? null
  return null
}

function toStanding(
  team: NonNullable<BracketMatch['participantA']>,
  rank: number,
): DerivedStanding {
  return {
    rank,
    teamId: team.teamId,
    teamLabel: team.teamLabel,
    members: (team.members ?? []).map((m) => ({
      userId: m.userId,
      displayName: m.displayName ?? null,
      avatarUrl: m.avatarUrl ?? null,
    })),
  }
}

const PODIUM_META: Record<number, { label: string; accent: string; glow: string; heightClass: string }> = {
  1: {
    label: 'Champion',
    accent: 'var(--tv-gold)',
    glow: 'rgba(255, 179, 0, 0.45)',
    heightClass: 'h-[clamp(180px,28vh,280px)]',
  },
  2: {
    label: 'Runner-up',
    accent: 'var(--tv-silver)',
    glow: 'rgba(200, 205, 212, 0.35)',
    heightClass: 'h-[clamp(140px,22vh,220px)]',
  },
  3: {
    label: 'Third place',
    accent: 'var(--tv-bronze)',
    glow: 'rgba(205, 127, 50, 0.35)',
    heightClass: 'h-[clamp(110px,17vh,170px)]',
  },
}

function PodiumColumn({ podium, delay }: { podium: PodiumEntry; delay: number }) {
  return (
    <motion.div
      // Initial state: below the floor + invisible. Settle to base.
      initial={{ opacity: 0, transform: 'translateY(80%)' }}
      animate={{ opacity: 1, transform: 'translateY(0%)' }}
      transition={{
        delay,
        // Spring with subtle bounce — permitted ONLY for this peak
        // moment per Emil. Higher bounce would tip into cartoonish.
        type: 'spring',
        duration: 0.7,
        bounce: 0.22,
      }}
      // Bumped gap: previously the team-name baseline sat right on
      // top of the pillar's gold border. Min 24px reads as a real
      // pedestal at 1080p; clamp scales upward for 4K.
      className="flex flex-col items-center gap-[clamp(24px,3vh,52px)]"
      style={{
        // Set the column order: 1st centre, 2nd left, 3rd right.
        order: podium.rank === 1 ? 2 : podium.rank === 2 ? 1 : 3,
      }}
    >
      <PodiumHero podium={podium} />
      <PodiumPillar podium={podium} />
    </motion.div>
  )
}

function PodiumHero({ podium }: { podium: PodiumEntry }) {
  const isChampion = podium.rank === 1
  const ringSize = isChampion
    ? 'clamp(110px, 10vw, 180px)'
    : 'clamp(90px, 8vw, 140px)'
  return (
    <div className="flex flex-col items-center gap-[clamp(8px,1vh,14px)]">
      <div className="flex flex-col items-center gap-2">
        <span
          className="font-display font-extrabold uppercase"
          style={{
            color: podium.accent,
            letterSpacing: '0.3em',
            fontSize: isChampion ? 'clamp(0.9rem, 1.1vw, 1.15rem)' : 'clamp(0.75rem, 0.9vw, 0.95rem)',
          }}
        >
          {podium.label}
        </span>
        <div className="flex items-center gap-[clamp(4px,0.4vw,8px)]">
          {podium.avatars.map((a) => (
            <div
              key={a.userId}
              className="flex items-center justify-center overflow-hidden rounded-full"
              style={{
                width: ringSize,
                height: ringSize,
                border: `${isChampion ? 'clamp(4px, 0.3vw, 6px)' : 'clamp(3px, 0.22vw, 4px)'} solid ${podium.accent}`,
                backgroundColor: 'var(--tv-bg-surface)',
                boxShadow: `0 0 ${isChampion ? '60px' : '40px'} -10px ${podium.glowColor}`,
              }}
            >
              {a.url ? (
                <img src={a.url} alt="" className="h-full w-full object-cover" />
              ) : (
                <span
                  className="font-display font-black"
                  style={{
                    color: podium.accent,
                    fontSize: isChampion ? 'clamp(1.6rem, 2.4vw, 2.8rem)' : 'clamp(1.2rem, 1.8vw, 2rem)',
                  }}
                >
                  {a.initials}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      <span
        className="font-display max-w-[clamp(180px,16vw,280px)] truncate font-extrabold"
        style={{
          color: 'var(--tv-fg)',
          fontSize: isChampion ? 'clamp(1.4rem, 1.8vw, 2.1rem)' : 'clamp(1.05rem, 1.4vw, 1.55rem)',
          textAlign: 'center',
        }}
      >
        {podium.teamName}
      </span>
    </div>
  )
}

function PodiumPillar({ podium }: { podium: PodiumEntry }) {
  return (
    <div
      className={`${podium.heightClass} relative w-[clamp(160px,14vw,260px)] overflow-hidden rounded-t-2xl`}
      style={{
        backgroundColor: 'var(--tv-bg-elev)',
        borderTop: `clamp(4px, 0.3vw, 6px) solid ${podium.accent}`,
        boxShadow: `0 -8px 40px -16px ${podium.glowColor}`,
      }}
    >
      {/* Big rank number painted as a subtle background mark. */}
      <span
        className="font-display absolute inset-0 flex items-center justify-center font-black"
        style={{
          color: podium.accent,
          opacity: 0.18,
          fontSize: 'clamp(6rem, 12vw, 14rem)',
          letterSpacing: '-0.05em',
          lineHeight: 1,
        }}
        aria-hidden
      >
        {podium.rank}
      </span>
      {/* Position label across the top — clear at distance. */}
      <span
        className="font-display absolute top-0 right-0 left-0 pt-[clamp(8px,1vh,14px)] text-center font-extrabold uppercase"
        style={{
          color: podium.accent,
          letterSpacing: '0.3em',
          fontSize: 'clamp(0.7rem, 0.85vw, 0.95rem)',
        }}
      >
        #{podium.rank}
      </span>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-3 pb-20">
      <span
        className="font-display font-medium uppercase"
        style={{
          color: 'var(--tv-fg-subtle)',
          letterSpacing: '0.3em',
          fontSize: 'clamp(0.85rem, 1vw, 1.05rem)',
        }}
      >
        Standings pending
      </span>
    </div>
  )
}
